import React, { memo, useEffect, useState } from 'react';
import Link from 'components/Link';
import PropTypes from 'prop-types';
import ImageV2 from 'components_v2/common/ImageV2';
import analyticsWrapper from 'helpers/analytics/analyticsWrapper';
import PopupOverlay from 'components_v2/common/PopupOverlay/PopupOverlay';
import Video from 'components/Video/Video';
import StoryImage from './StoryImage';
import StoryContent from './StoryContent';
import ShareComponent from './ShareComponent';
import s from './NewStoryCard.scss';
import { IMAGE_URL } from '../../../constants';
import { CARD_10, CARD_11, CARD_22, CARD_14, CARD_11_1 } from './cards';

function NewStoryCard(props) {
  const {
    // Core story data
    title,
    msid,
    des,
    wu,
    tn,
    syn,
    subTitle,
    sectionNameTop,
    sectionName,
    createdTime,
    createdTime2,
    hasVideo,
    imagelist,
    cs,

    // Card styling
    cardType = '',
    isLarge = '',
    isBigHeading = false,
    isVideoActive = false,
    hideContent = false,
    addTitleAsHeading = false,

    // Image config
    imagesize,
    imageUrl,
    width = 600,
    imageHeight,
    shouldNotlazyloadImage = false,
    imagePlaceHolderSrc = '',
    usePosterImage = false,

    // Icons and media
    iconClass,
    cmstype,
    videoTime,
    showIIcon = false,
    showPlayIconAlso = false,
    slikeId,

    // Navigation
    target = '',
    forceTargetBlank = false,

    // Analytics
    ga4Type = '',
    ga4Label = '',
    ga4SubLabel = '',
    ga4Category = '',
    gaIndex = 0,
    index,

    // Ratings
    criticRating,
    userRating,
    userCount,
    buttonText,

    // Movie data
    movieCardData,

    // Interactions
    onClick,
    onCriticRatingClick,
    onUserRatingClick,
    onVideoIconClick,
    showPopup = false,
    showHoveredVideo,
    doNotStopClickPropagation = false,

    // Special features
    fromToiPrint = false,
    synStyle = {},
    updateplayerConfig = { skipReco: true },
    isPhotostoryList,
  } = props;

  // Component state
  const [hoveredSlikeId, setHoveredSlikeId] = useState(false);

  useEffect(
    () => {
      if (showHoveredVideo) {
        setHoveredSlikeId(slikeId);
      }
    },
    [showHoveredVideo],
  );

  const [showDes, setShowDes] = useState(false);
  const [showBannerPopup, setShowBannerPopup] = useState(false);
  const [bannerPopup, setBannerPopup] = useState(null);

  // Derive content type and icon from tn if not explicitly provided
  const derivedCmstype =
    cmstype ||
    (tn &&
      {
        photostory: 'PHOTOGALLERYSLIDESHOWSECTION',
        video: 'MEDIAVIDEO',
        liveblog: 'LIVEBLOG',
      }[tn]) ||
    '';

  let derivedIconClass =
    iconClass ||
    (tn &&
      {
        photostory: 'photoIcon',
        video: 'videoshowIcon',
      }[tn]) ||
    '';

  derivedIconClass = hasVideo ? 'videoshowIcon' : derivedIconClass;
  derivedIconClass = cs ? 'toiPlusIcon' : derivedIconClass;

  // Image configuration
  const shouldUsePortraitMode =
    cardType !== 'card_9' &&
    (derivedCmstype === 'PHOTOGALLERYSLIDESHOWSECTION' ||
      derivedCmstype === 'MOVIEREVIEW' ||
      tn === 'movie reviews' ||
      tn === 'movie-review' ||
      usePosterImage);

  const resizemode = shouldUsePortraitMode ? 75 : 4;
  let height = shouldUsePortraitMode ? imageHeight || 400 : undefined;

  if (isPhotostoryList) {
    height = 400;
  }

  // Card types that don't show images
  const inlineCard = [CARD_10, CARD_11, CARD_22, CARD_14, CARD_11_1].includes(
    cardType,
  );

  const onError = e => {
    e.target.onerror = null;
    e.target.src = imagePlaceHolderSrc || `${IMAGE_URL}34824568.cms`;
  };

  // Event handlers
  const handleShowDesInCard = () => setShowDes(!showDes);

  const handleClosePopup = () => {
    // eslint-disable-next-line no-unused-expressions
    document.querySelector?.('body')?.classList?.remove('overflowhidden');
    setShowBannerPopup(false);
  };

  const handleBannerClick = banner => {
    // eslint-disable-next-line no-unused-expressions
    document.querySelector?.('body')?.classList?.add('overflowhidden');
    setShowBannerPopup(true);
    setBannerPopup(banner);
  };

  const handleCardClick = e => {
    if (typeof onClick === 'function') {
      if (!doNotStopClickPropagation) {
        e.preventDefault();
        e.stopPropagation();
      }
      onClick();
      return false;
    }

    // Analytics tracking
    if (ga4Type) {
      analyticsWrapper('ga4', {
        event: 'user_click',
        type: ga4Type,
        category: ga4Category || 'story_click',
        story_no: `${index?.gaindex || gaIndex}`,
        msid,
        label: ga4Label,
        sublabel: ga4SubLabel,
      });
    }

    // Handle popup for banner cards
    if (showPopup) {
      e.preventDefault();
      const data = {
        imageid: msid,
        slikeId,
        id: msid,
        resizemode,
      };
      handleBannerClick(data);
      return false;
    }

    // Prevent default for info icons and share buttons
    try {
      if (showIIcon || e.target.dataset.ga?.includes('share')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    } catch (error) {
      // Silent catch for missing dataset
    }

    return true;
  };

  // Popup rendering for banner cards
  const renderPopupImage = () => (
    <PopupOverlay
      position="center"
      closeCallback={handleClosePopup}
      show={showBannerPopup}
      blackOverlayEnabled
      fixWidth={!!bannerPopup.slikeId}
    >
      {bannerPopup.slikeId ? (
        <Video
          msid={bannerPopup.id}
          autoplay
          slikeId={bannerPopup.slikeId}
          playerType={bannerPopup.playerType}
          videoClassName="popup_video_wrapper"
          allowDim={false}
          source="USNHP_MainVideoWidget"
          updateplayerConfig={updateplayerConfig}
        />
      ) : (
        <div className={s.bannerPopupImage}>
          <ImageV2
            msid={bannerPopup.id}
            resizemode={4}
            imgsize={bannerPopup.imagesize}
            height="620"
            alt={bannerPopup.hl}
            title={bannerPopup.title || ''}
            placeHolderSrc={imagePlaceHolderSrc || `${IMAGE_URL}123379562.cms`}
            onError={onError}
          />
        </div>
      )}
    </PopupOverlay>
  );

  // Main content rendering with extracted components
  const renderContent = () => (
    <>
      {!inlineCard && (
        <StoryImage
          msid={msid}
          width={width}
          height={height}
          imagesize={imagesize}
          resizemode={resizemode}
          cardType={cardType}
          hoveredSlikeId={hoveredSlikeId}
          gaIndex={gaIndex}
          slikeId={slikeId}
          imageUrl={imageUrl}
          shouldNotlazyloadImage={shouldNotlazyloadImage}
          imagePlaceHolderSrc={imagePlaceHolderSrc}
          iconClass={derivedIconClass}
          videoTime={videoTime}
          showPlayIconAlso={showPlayIconAlso}
          cmstype={derivedCmstype}
          criticRating={criticRating}
          showIIcon={showIIcon}
          des={des}
          showDes={showDes}
          onVideoIconClick={onVideoIconClick}
          onShowDesInCard={handleShowDesInCard}
          imagelist={imagelist}
          buttonText={buttonText}
        />
      )}

      {!hideContent && (
        <>
          <StoryContent
            cardType={cardType}
            iconClass={derivedIconClass}
            videoTime={videoTime}
            cmstype={derivedCmstype}
            title={title}
            subTitle={subTitle}
            addTitleAsHeading={addTitleAsHeading}
            movieCardData={movieCardData}
            syn={syn}
            synStyle={synStyle}
            createdTime2={createdTime2}
            createdTime={createdTime}
            sectionName={sectionName}
            hasVideo={hasVideo}
            inlineCard={inlineCard}
            sectionNameTop={sectionNameTop}
            criticRating={criticRating}
            userRating={userRating}
            userCount={userCount}
            onCriticRatingClick={onCriticRatingClick}
            onUserRatingClick={onUserRatingClick}
            buttonText={buttonText}
          />

          {fromToiPrint && <ShareComponent title={title} wu={wu} />}
        </>
      )}
    </>
  );

  // Build CSS classes
  const cardClasses = [
    'border_color',
    s.storyCard,
    isLarge && s.isLarge,
    cardType && s[cardType],
    isBigHeading && s.bigHeading,
    isVideoActive && s.videoActive,
    !wu && s.noLink,
  ]
    .filter(Boolean)
    .join(' ');

  const commonProps = slikeId
    ? {
        className: cardClasses,
        ...(showHoveredVideo === false && {
          onMouseEnter: () => setHoveredSlikeId(slikeId),
          onMouseLeave: () => setHoveredSlikeId(false),
        }),
        onClick: handleCardClick,
      }
    : {
        className: cardClasses,
        onClick: handleCardClick,
      };

  return (
    <>
      {/* Popup for banner cards */}
      {bannerPopup &&
        showBannerPopup &&
        bannerPopup?.imageid &&
        renderPopupImage()}

      {/* Main card */}
      {wu ? (
        <Link
          to={wu}
          target={target || '_blank'}
          forceTargetBlank={forceTargetBlank}
          {...commonProps}
        >
          {renderContent()}
        </Link>
      ) : (
        <div {...commonProps} className={`${cardClasses} ${s.normalStoryCard}`}>
          {renderContent()}
        </div>
      )}
    </>
  );
}

NewStoryCard.propTypes = {
  // Core story data
  title: PropTypes.string.isRequired,
  msid: PropTypes.string.isRequired,
  des: PropTypes.string,
  wu: PropTypes.string,
  tn: PropTypes.string,
  syn: PropTypes.string,
  subTitle: PropTypes.string,
  sectionName: PropTypes.string,
  createdTime: PropTypes.string,
  createdTime2: PropTypes.string,

  // Card styling
  cardType: PropTypes.string,
  isLarge: PropTypes.string,
  isBigHeading: PropTypes.bool,
  isVideoActive: PropTypes.bool,
  hideContent: PropTypes.bool,
  addTitleAsHeading: PropTypes.bool,

  // Image config
  imagesize: PropTypes.string,
  imageUrl: PropTypes.string,
  width: PropTypes.number,
  imageHeight: PropTypes.number,
  shouldNotlazyloadImage: PropTypes.bool,
  imagePlaceHolderSrc: PropTypes.string,
  usePosterImage: PropTypes.bool,

  // Icons and media
  iconClass: PropTypes.string,
  cmstype: PropTypes.string,
  videoTime: PropTypes.string,
  showIIcon: PropTypes.bool,
  showPlayIconAlso: PropTypes.bool,
  slikeId: PropTypes.string,

  // Navigation
  target: PropTypes.string,
  forceTargetBlank: PropTypes.bool,

  // Analytics
  ga4Type: PropTypes.string,
  ga4Label: PropTypes.string,
  ga4Category: PropTypes.string,
  gaIndex: PropTypes.number,
  index: PropTypes.objectOf(PropTypes.any),

  // Ratings
  criticRating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userRating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  // Movie data
  movieCardData: PropTypes.objectOf(PropTypes.any),

  // Interactions
  onClick: PropTypes.func,
  onCriticRatingClick: PropTypes.func,
  onUserRatingClick: PropTypes.func,
  onVideoIconClick: PropTypes.func,
  showPopup: PropTypes.bool,
  doNotStopClickPropagation: PropTypes.bool,

  // Special features
  fromToiPrint: PropTypes.bool,
  synStyle: PropTypes.objectOf(PropTypes.any),
  buttonText: PropTypes.string,
};

NewStoryCard.defaultProps = {
  des: '',
  wu: '',
  tn: '',
  syn: '',
  subTitle: '',
  sectionName: '',
  createdTime: '',
  createdTime2: '',
  cardType: '',
  isLarge: '',
  isBigHeading: false,
  isVideoActive: false,
  hideContent: false,
  addTitleAsHeading: false,
  imagesize: '',
  imageUrl: '',
  width: 600,
  imageHeight: undefined,
  shouldNotlazyloadImage: false,
  imagePlaceHolderSrc: '',
  usePosterImage: false,
  iconClass: '',
  cmstype: '',
  videoTime: '',
  showIIcon: false,
  showPlayIconAlso: false,
  slikeId: '',
  target: '',
  forceTargetBlank: false,
  ga4Type: '',
  ga4Label: '',
  ga4Category: '',
  gaIndex: 0,
  index: null,
  criticRating: '',
  userRating: '',
  userCount: '',
  movieCardData: null,
  onClick: null,
  onCriticRatingClick: null,
  onUserRatingClick: null,
  onVideoIconClick: null,
  showPopup: false,
  doNotStopClickPropagation: false,
  fromToiPrint: false,
  synStyle: {},
  buttonText: null,
};

export default memo(NewStoryCard);
